/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';

import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import {
	MatSnackBar,
	MatSnackBarHorizontalPosition,
	MatSnackBarVerticalPosition,
} from '@angular/material/snack-bar';
import { NEW_VERSION_AVAILABLE, REFRESH_TO_UPGRADE } from '@touchplan/strings';

import { EMPTY, merge, Observable } from 'rxjs';
import { switchMap, skip, tap, distinctUntilChanged, filter } from 'rxjs/operators';
import { ENV_CONFIG } from 'src/app/app-data/constants/dynamic-env';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import { MatDialog } from '@angular/material/dialog';
import { PlatformConfiguration } from '../platform-configuration/platform-configuration.service';
import { ForceRefreshDialogComponent } from 'src/app/common/application-wide-message/components/force-refresh-dialog/force-refresh-dialog.component';
import { ILoggerService } from '@touchplan/logging';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';

import firebase from 'firebase/compat/app';

@Injectable()
export class ReloadMonitoring {
	private horizontalPosition: MatSnackBarHorizontalPosition = 'center';
	private verticalPosition: MatSnackBarVerticalPosition = 'top';
	private logger: ILoggerService;

	constructor(
		private auth: AngularFireAuth,
		private db: AngularFireDatabase,
		private snackBar: MatSnackBar,
		private dialog: MatDialog,
		private platformConfiguration: PlatformConfiguration
	) {
		this.logger = LoggerServiceFactory.create('ReloadMonitoring');
	}

	public start() {
		const globalSignalPath = fancyFirebaseGetters.getSignalsPath('reload');
		const usersSignalsPath = fancyFirebaseGetters.getSignalsPath('users');
		merge(
			this.db
				.object(fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(globalSignalPath))
				.valueChanges()
				.pipe(
					tap((thing) => this.logger.log('signal', thing)),
					skip(1)
				),
			this.auth.user.pipe(
				switchMap((user) => {
					if (!user) {
						return EMPTY;
					}
					return this.db
						.object(
							fancyFirebaseReferencer
								.getLegacyRef<firebase.database.Reference>(usersSignalsPath)
								.child(user.uid)
								.child('reload')
						)
						.valueChanges()
						.pipe(skip(1));
				})
			)
		).subscribe((__) => {
			window.location.reload();
		});
	}

	public openRefreshNowSnackBarIfVersionsDoesNotMatch() {
		const versionOfWebsitePath = fancyFirebaseGetters.getSignalsPath(
			this.platformConfiguration.refreshNowNagPath
		);
		this.db
			.object(fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(versionOfWebsitePath))
			.valueChanges()
			.pipe(
				distinctUntilChanged(),
				filter((value) => value !== ENV_CONFIG.shortSha),
				switchMap(() => {
					return this.openRefreshNowUI();
				})
			)
			.subscribe(() => {
				window.location.reload();
			});
	}

	private openRefreshNowUI(): Observable<any> {
		// TODO:  Un-comment the following line and comment-out the modal dialog one when we want to use the snackbar message again
		// return this.openRefreshNowSnackBar();
		return this.openRefreshNowModalDialog();
	}

	private openRefreshNowSnackBar(): Observable<any> {
		const snackBarRef = this.snackBar.open(NEW_VERSION_AVAILABLE, REFRESH_TO_UPGRADE.toUpperCase(), {
			horizontalPosition: this.horizontalPosition,
			verticalPosition: this.verticalPosition,
			panelClass: ['app-wide-msg-content'],
		});
		return snackBarRef.onAction();
	}

	private openRefreshNowModalDialog(): Observable<any> {
		const dialogRef = this.dialog.open(ForceRefreshDialogComponent, {
			width: '337px',
			height: '275px',
			disableClose: true,
		});
		return dialogRef.afterClosed();
	}
}
